import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Plus } from 'lucide-react';
import { z } from 'zod';

import { Page } from '@/components/layout/Page';
import { Section } from '@/components/layout/Section';
import { PageHeader } from '@/components/composed/PageHeader';
import { RouteError } from '@/components/composed/RouteError';
import { NotFound } from '@/components/composed/NotFound';
import { FilterBar } from '@/components/composed/FilterBar';
import { EmptyState } from '@/components/composed/EmptyState';
import { FormDialog } from '@/components/composed/FormDialog';
import { FormField } from '@/components/composed/FormField';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  CreateEntityConfirm,
  entityListQuery,
  entityCreateFormSchema,
  toEntityCreateInput,
  sanitizeTin,
} from '@/features/entities';
import type {
  Entity,
  EntityCreateInput,
  EntityKind,
  EntityListParams,
} from '@/lib/api/domains/entities';
import { useQuery } from '@tanstack/react-query';
import { ActGate } from '@/components/composed/ActGate';
import { CREATABLE_KINDS } from '@/lib/entity-composition';
import { useAuthStore } from '@/lib/session/auth-store';
import { usePageMeta } from '@/app/shell/use-page-meta';

const searchSchema = z.object({
  page: z.number().int().min(1).catch(1),
  pageSize: z.number().int().min(1).max(100).catch(25),
  q: z.string().optional(),
});

export const Route = createFileRoute('/_auth/entities/')({
  validateSearch: searchSchema,
  loaderDeps: ({ search }) => search,
  loader: ({ context, deps }) =>
    context.queryClient.ensureQueryData(entityListQuery(toParams(deps))),
  component: EntitiesPage,
  errorComponent: RouteError,
  notFoundComponent: NotFound,
});

function toParams(search: z.infer<typeof searchSchema>): EntityListParams {
  return {
    page: search.page,
    pageSize: search.pageSize,
    ...(search.q ? { q: search.q } : {}),
  };
}

/*
 * The decided creatable-kind pick-list is the ONE source in
 * lib/entity-composition (R126): the four decided kinds — Client, Agency, Broker
 * (the Agency kind in a legal form), Provider (carrier; a partner platform is a
 * Provider, AC-50446af0). No path to a second `Software` entity (AC-e3fc9295),
 * no fifth `TPA` kind (AC-40d18696, BLOCKED — CR-E02-08). The index renders the
 * shared list rather than its own copy, so the create door and the composition
 * can never disagree about the kinds.
 */
function EntitiesPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const session = useAuthStore((s) => s.session);
  const { data } = useQuery(entityListQuery(toParams(search)));

  // The create form collects the two-fact minimum, then the pre-commit
  // confirmation (R21) names what saving commits Kelly to before the create is
  // sent. "draft" holds the collected input between the form and the confirm.
  const [formOpen, setFormOpen] = useState(false);
  // The pick-list offers four labels; two of them (Agency, Broker) create the
  // same `Agency` enum value (Broker is the Agency kind in a legal form, R35/
  // R126). State tracks the chosen LABEL so the two stay distinct in the UI, and
  // the enum value is derived at submit. There is no `Software` or `TPA` label,
  // so neither can be chosen (AC-e3fc9295, AC-40d18696).
  const [kindLabel, setKindLabel] = useState<string>(CREATABLE_KINDS[0]!.label);
  const kind: EntityKind =
    CREATABLE_KINDS.find((k) => k.label === kindLabel)?.value ?? 'Client';
  const [legalName, setLegalName] = useState('');
  const [tin, setTin] = useState('');
  const [draft, setDraft] = useState<EntityCreateInput | null>(null);
  // A field's inline error shows only once the user has touched it, so a
  // freshly opened dialog does not shout "required" before anyone has typed.
  const [nameTouched, setNameTouched] = useState(false);
  const [tinTouched, setTinTouched] = useState(false);

  usePageMeta({ title: 'Organizations' });

  // One source of truth for both the disabled-submit gate and the inline
  // messages: the client-side schema mirrors the domain server's rule EXACTLY
  // (R21 — a UX gate, not a replacement for the server authority).
  const parsed = entityCreateFormSchema.safeParse({ kind, legalName, tin });
  function fieldErrors(path: 'legalName' | 'tin'): string[] {
    if (parsed.success) return [];
    return parsed.error.issues
      .filter((issue) => issue.path[0] === path)
      .map((issue) => issue.message);
  }

  function setSearch(patch: Partial<z.infer<typeof searchSchema>>) {
    void navigate({ search: (prev) => ({ ...prev, ...patch }) });
  }

  function openForm() {
    setKindLabel(CREATABLE_KINDS[0]!.label);
    setLegalName('');
    setTin('');
    setNameTouched(false);
    setTinTouched(false);
    setFormOpen(true);
  }

  // The form does NOT create — it collects, then hands to the confirmation,
  // which is the only thing that runs the create (R21, AC-ed17e8e1). An invalid
  // draft never reaches the confirm: client validation is the UX gate.
  function reviewCreate() {
    if (!parsed.success) return;
    setDraft(toEntityCreateInput(parsed.data));
    setFormOpen(false);
  }

  const items = data?.items ?? [];

  return (
    <Page bleed>
      <PageHeader
        title="Organizations"
        actions={
          /*
           * The create way-in is the F04 entity-edit act (Administrator-only),
           * read per-act from the server-sourced capabilities. A
           * `tpabilling_user` (Viewer) sees the list but is NOT offered create
           * (E02.F04.R5, AC-9311d02c); the read surface stays visible
           * (AC-fea4d1bf). Courtesy only; the domain gate refuses regardless.
           */
          <ActGate session={session} act="tpabilling.enrollment.edit">
            <Button onClick={openForm}>
              <Plus className="size-4" />
              New organization
            </Button>
          </ActGate>
        }
      />

      <Section>
        <FilterBar
          search={search.q ?? ''}
          onSearchChange={(q) => setSearch({ q: q || undefined, page: 1 })}
          searchLabel="Search"
          searchPlaceholder="Search by name…"
          onClear={
            search.q ? () => setSearch({ q: undefined, page: 1 }) : undefined
          }
        />

        {items.length === 0 ? (
          <EmptyState
            title="No organizations"
            description="No organizations match."
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Legal name</TableHead>
                <TableHead>Kind</TableHead>
                <TableHead>Public id</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((entity: Entity) => (
                <TableRow
                  key={entity.id}
                  onClick={() =>
                    void navigate({
                      to: '/entities/$entityId',
                      params: { entityId: entity.id },
                    })
                  }
                  className="cursor-pointer"
                >
                  <TableCell>{entity.legalName}</TableCell>
                  <TableCell>{entity.kind}</TableCell>
                  <TableCell>{entity.publicId}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Section>

      <FormDialog
        open={formOpen}
        onOpenChange={setFormOpen}
        title="New organization"
        submitLabel="Review"
        submitDisabled={!parsed.success}
        onSubmit={reviewCreate}
      >
        <FormField label="Kind">
          {({ id }) => (
            <Select
              value={kindLabel}
              onValueChange={(v) => setKindLabel(String(v))}
            >
              <SelectTrigger id={id}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CREATABLE_KINDS.map((k) => (
                  <SelectItem key={k.label} value={k.label}>
                    {k.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </FormField>
        <FormField
          label="Legal name"
          errors={nameTouched ? fieldErrors('legalName') : []}
        >
          {({ id }) => (
            <Input
              id={id}
              value={legalName}
              onChange={(e) => setLegalName(e.target.value)}
              onBlur={() => setNameTouched(true)}
            />
          )}
        </FormField>
        <FormField
          label="Tax identifier"
          errors={tinTouched ? fieldErrors('tin') : []}
        >
          {({ id }) => (
            <Input
              id={id}
              value={tin}
              onChange={(e) => {
                setTinTouched(true);
                setTin(sanitizeTin(e.target.value));
              }}
              onBlur={() => setTinTouched(true)}
            />
          )}
        </FormField>
      </FormDialog>

      {draft ? (
        <CreateEntityConfirm
          input={draft}
          open={Boolean(draft)}
          onOpenChange={(open) => {
            if (!open) setDraft(null);
          }}
        />
      ) : null}
    </Page>
  );
}
